import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SERVICES = [
  {
    icon: 'ri-car-line',
    title: 'Vehicle Registration',
    slug: '/services/vehicle-registration',
    items: [
      'New vehicle registration (private party & dealer)',
      'Annual registration renewals',
      'Out-of-state vehicle transfers',
      'Fleet & commercial registrations',
    ],
  },
  {
    icon: 'ri-file-transfer-line',
    title: 'Title Transfer',
    slug: '/services/title-transfer',
    items: [
      'Private party title transfers',
      'Dealership & lender title releases',
      'Gift & family transfers',
      'Inheritance & estate title changes',
    ],
  },
  {
    icon: 'ri-bank-card-line',
    title: 'License Plates',
    slug: '/services/license-plates',
    items: [
      'Standard Nevada plates',
      'Specialty & organization plates',
      'Personalized vanity plates',
      'Replacement & plate transfers',
    ],
  },
  {
    icon: 'ri-file-copy-line',
    title: 'Duplicate Title',
    slug: '/services/duplicate-title',
    items: [
      'Lost title replacement (VP-012)',
      'Stolen title — affidavit assistance',
      'Damaged title replacement',
      'Lien release & title corrections',
    ],
  },
  {
    icon: 'ri-time-line',
    title: 'Temporary Permits',
    slug: '/services/temporary-permits',
    items: [
      '10-day permit — NV-registered vehicles',
      '30-day permit — non-NV vehicles',
      'In-transit movement permits',
      'Dealer transport tags',
    ],
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Notary Services',
    slug: '/services/notary',
    items: [
      'Title notarization',
      'Bill of sale & odometer disclosure',
      'Affidavit & power of attorney',
      'General DMV document notarization',
    ],
  },
];

const QUICK_LINKS = [
  { label: 'Vehicle Registration', icon: 'ri-car-line', to: '/services/vehicle-registration' },
  { label: 'Title Transfer', icon: 'ri-file-transfer-line', to: '/services/title-transfer' },
  { label: 'License Plates', icon: 'ri-bank-card-line', to: '/services/license-plates' },
  { label: 'Temporary Permits', icon: 'ri-time-line', to: '/services/temporary-permits' },
  { label: 'Duplicate Title', icon: 'ri-file-copy-line', to: '/services/duplicate-title' },
  { label: 'Notary Services', icon: 'ri-quill-pen-line', to: '/services/notary' },
  { label: 'DMV Forms', icon: 'ri-file-list-3-line', to: '/forms' },
  { label: 'Fees & Pricing', icon: 'ri-money-dollar-circle-line', to: '/fees' },
];

const ACCORDION_ITEMS = [
  {
    title: 'Vehicle Registration',
    links: [
      { label: 'New vehicle registration (private party & dealer)', to: '/services/vehicle-registration' },
      { label: 'Annual registration renewals', to: '/services/vehicle-registration' },
      { label: 'Out-of-state vehicle transfers (30-day window)', to: '/services/vehicle-registration' },
      { label: 'Fleet & commercial registrations', to: '/services/vehicle-registration' },
    ],
  },
  {
    title: 'Title & Ownership',
    links: [
      { label: 'Private party title transfers', to: '/services/title-transfer' },
      { label: 'Dealership & lender title releases', to: '/services/title-transfer' },
      { label: 'Gift & family transfers', to: '/services/title-transfer' },
      { label: 'Duplicate / lost title replacement', to: '/services/duplicate-title' },
    ],
  },
  {
    title: 'License Plates',
    links: [
      { label: 'Standard Nevada plates', to: '/services/license-plates' },
      { label: 'Specialty & organization plates', to: '/services/license-plates' },
      { label: 'Personalized vanity plates (availability check)', to: '/services/license-plates' },
      { label: 'Replacement & plate transfers', to: '/services/license-plates' },
    ],
  },
  {
    title: 'Permits & Notary',
    links: [
      { label: '10-day permit — NV-registered vehicles', to: '/services/temporary-permits' },
      { label: '30-day permit — non-NV vehicles', to: '/services/temporary-permits' },
      { label: 'Title & document notarization', to: '/services/notary' },
      { label: 'Affidavit & power of attorney notarization', to: '/services/notary' },
    ],
  },
];

const HELP_CARDS = [
  {
    title: 'New to Nevada?',
    body: 'Welcome to the Silver State! New residents must register their out-of-state vehicle within 30 days of establishing Nevada residency. We handle the VIN inspection, emissions coordination, and all paperwork.',
    link: '/services/vehicle-registration',
    linkLabel: 'New Resident Registration',
    icon: 'ri-map-pin-line',
  },
  {
    title: 'Buying or Selling a Vehicle?',
    body: 'Private party sales require a properly signed title, bill of sale, and odometer disclosure. We prepare every form correctly and provide on-site notarization so the transfer is done right the first time.',
    link: '/services/title-transfer',
    linkLabel: 'Title Transfer Services',
    icon: 'ri-exchange-line',
  },
  {
    title: 'Lost or Damaged Title?',
    body: 'We file Nevada Form VP-012 (Application for Duplicate Title) on your behalf. Bring your photo ID and current registration — we handle the rest, including any required affidavits.',
    link: '/services/duplicate-title',
    linkLabel: 'Duplicate Title Services',
    icon: 'ri-file-copy-line',
  },
  {
    title: 'Need a Temporary Permit?',
    body: 'Nevada issues 10-day permits for NV-registered vehicles and 30-day permits for vehicles not yet registered in Nevada. Same-day processing available — call ahead to confirm availability.',
    link: '/services/temporary-permits',
    linkLabel: 'Temporary Permit Services',
    icon: 'ri-time-line',
  },
  {
    title: 'Fleet & Commercial Vehicles',
    body: 'We process multiple commercial vehicle registrations in a single appointment. Bring all titles, insurance cards, and VINs — we prepare every form and submit to the Nevada DMV on your behalf.',
    link: '/services/vehicle-registration',
    linkLabel: 'Fleet Registration',
    icon: 'ri-truck-line',
  },
  {
    title: 'DMV Forms & Downloads',
    body: 'Download official Nevada DMV VP-series forms — registration, title, bill of sale, duplicate title, permits, power of attorney, and more. All forms link directly to dmv.nv.gov.',
    link: '/forms',
    linkLabel: 'View All DMV Forms',
    icon: 'ri-file-list-3-line',
  },
];

const STEPS = [
  { num: '01', icon: 'ri-phone-line', title: 'Call to Schedule', desc: 'Call 702.802.8696 to book your appointment. Tell us which service you need and we\'ll tell you exactly what to bring.' },
  { num: '02', icon: 'ri-file-list-3-line', title: 'Gather Documents', desc: 'We provide a precise checklist for your service type. No guessing, no rejected applications, no wasted trips.' },
  { num: '03', icon: 'ri-building-2-line', title: 'Visit Our Office', desc: 'Come to 4525 S Sandhill Rd, Las Vegas NV. We review your documents, prepare all DMV paperwork, and process your transaction.' },
  { num: '04', icon: 'ri-check-double-line', title: 'Done — Drive Away', desc: 'Leave with your registration, plates, title, or permit processed. Most services completed same day.' },
];

const TESTIMONIALS = [
  { name: 'Marcus T.', role: 'Las Vegas Resident', date: 'March 2025', rating: 5, text: 'Transferred my Texas car to Nevada in under an hour. They told me exactly what to bring — showed up, got it done. No DMV line, no stress.' },
  { name: 'Jennifer L.', role: 'Henderson, NV', date: 'February 2025', rating: 5, text: 'I was dreading the title transfer for my late husband\'s vehicle. The staff were compassionate and handled the notarization quickly. Couldn\'t have done it without them.' },
  { name: 'Carlos R.', role: 'Fleet Manager, Las Vegas', date: 'January 2025', rating: 5, text: 'Registered 3 commercial trucks in one afternoon. They knew every form, every fee. Saved my business hours of Nevada DMV headaches.' },
  { name: 'Patricia W.', role: 'North Las Vegas', date: 'December 2024', rating: 5, text: 'Got my personalized plates checked and my bill of sale notarized in one visit. Fast, professional, and priced fairly.' },
];

export default function HomePage() {
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const [appointmentSubmitted, setAppointmentSubmitted] = useState(false);
  const [appointmentError, setAppointmentError] = useState('');

  useEffect(() => {
    document.title = 'DMV-Xpress Services™ | Las Vegas Nevada — Vehicle Registration, Title Transfer, License Plates';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Licensed third-party DMV document preparation in Las Vegas, NV. Vehicle registration, title transfers, license plates, temporary permits & notary. NOT the Nevada DMV. Call 702.802.8696.');

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}/`);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.setAttribute('data-page-schema', 'home');
    schema.text = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'DMV-Xpress Services',
        url: import.meta.env.VITE_SITE_URL || '',
        description: 'Licensed third-party Nevada DMV document preparation service in Las Vegas, NV.',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${import.meta.env.VITE_SITE_URL || ''}/forms?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/#business`,
        name: 'DMV-Xpress Services',
        legalName: 'DMV-Xpress Services™',
        url: import.meta.env.VITE_SITE_URL || '',
        telephone: '+17028028696',
        email: 'info@dmv-xpress.com',
        description: 'Licensed third-party Nevada DMV document preparation service (NRS240A) in Las Vegas. NOT the Nevada DMV. Vehicle registration, title transfers, license plates, temporary permits, and notary services.',
        image: `${import.meta.env.VITE_SITE_URL || ''}/og-image.jpg`,
        priceRange: '$$',
        currenciesAccepted: 'USD',
        paymentAccepted: 'Cash, Credit Card, Debit Card',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '4525 S Sandhill Rd',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89121',
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 36.08398,
          longitude: -115.06516,
        },
        areaServed: [
          { '@type': 'City', name: 'Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Henderson' },
          { '@type': 'City', name: 'North Las Vegas' },
          { '@type': 'City', name: 'Summerlin' },
        ],
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '15:00' },
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'DMV Document Preparation Services',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vehicle Registration', url: `${import.meta.env.VITE_SITE_URL || ''}/services/vehicle-registration` } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Title Transfer', url: `${import.meta.env.VITE_SITE_URL || ''}/services/title-transfer` } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'License Plates', url: `${import.meta.env.VITE_SITE_URL || ''}/services/license-plates` } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Duplicate Title', url: `${import.meta.env.VITE_SITE_URL || ''}/services/duplicate-title` } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Temporary Permits', url: `${import.meta.env.VITE_SITE_URL || ''}/services/temporary-permits` } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Notary Services', url: `${import.meta.env.VITE_SITE_URL || ''}/services/notary` } },
          ],
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '5000',
          bestRating: '5',
          worstRating: '1',
        },
        review: [
          { '@type': 'Review', author: { '@type': 'Person', name: 'Marcus T.' }, reviewRating: { '@type': 'Rating', ratingValue: '5' }, reviewBody: 'Transferred my Texas car to Nevada in under an hour. They told me exactly what to bring — showed up, got it done. No DMV line, no stress.' },
          { '@type': 'Review', author: { '@type': 'Person', name: 'Carlos R.' }, reviewRating: { '@type': 'Rating', ratingValue: '5' }, reviewBody: 'Registered 3 commercial trucks in one afternoon. They knew every form, every fee. Saved my business hours of Nevada DMV headaches.' },
        ],
        sameAs: [],
      },
    ]);
    document.head.appendChild(schema);
    return () => {
      const existing = document.head.querySelector('script[data-page-schema="home"]');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const handleAppointment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAppointmentError('');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7de5b6m3fsfopf23r1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setAppointmentSubmitted(true); form.reset(); }
      else setAppointmentError('Submission failed. Please call 702.802.8696.');
    } catch {
      setAppointmentError('Network error. Please call 702.802.8696.');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Source Sans Pro', 'Segoe UI', sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: 580, backgroundColor: '#001f4d' }}>
        <img
          src="https://public.readdy.ai/ai/img_res/f2a44628e5d47e6e7fbdf938e58a6685.jpg"
          alt="Las Vegas Nevada DMV Services — DMV-Xpress"
          className="w-full h-full object-cover object-top absolute inset-0"
          style={{ minHeight: 580 }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,31,77,0.93) 0%, rgba(0,31,77,0.82) 50%, rgba(0,31,77,0.70) 100%)' }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,31,77,0.6))' }}></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center py-20 pt-16">
          <div
            className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full mb-6 uppercase tracking-widest"
            style={{ backgroundColor: 'rgba(232,119,34,0.25)', border: '1px solid rgba(232,119,34,0.5)', color: '#ffa85c' }}
          >
            <i className="ri-shield-check-fill" style={{ color: '#E87722' }}></i>
            Licensed 3rd Party Provider — NRS240A — NOT the Nevada DMV
          </div>

          <h1
            className="text-5xl md:text-7xl font-black text-white leading-tight mb-5"
            style={{ fontFamily: "'Inter', 'Source Sans Pro', sans-serif", letterSpacing: '-0.03em' }}
          >
            Las Vegas Nevada<br />
            <span style={{ color: '#E87722' }}>DMV-Xpress Services™</span>
          </h1>

          <p className="text-white/75 text-xl leading-relaxed mb-3 max-w-2xl">
            Licensed 3rd Party DMV Provider
          </p>
          <p className="text-white/60 text-base leading-relaxed mb-8 max-w-2xl">
            Fast, professional Nevada DMV services — vehicle registration, title transfers, license plates &amp; more.
            Most transactions completed in under 30 minutes.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            <a
              href="tel:7028028696"
              className="inline-flex items-center gap-2.5 text-white font-bold px-8 py-4 rounded-xl text-base transition-all whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #E87722 0%, #c45e10 100%)', boxShadow: '0 4px 20px rgba(232,119,34,0.4)' }}
            >
              <i className="ri-phone-fill text-lg"></i>
              Call Now — (702) 802-8696
            </a>
            <a
              href="tel:7028028696"
              className="inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-xl text-base transition-all whitespace-nowrap"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.3)', color: '#fff' }}
            >
              <i className="ri-calendar-check-fill text-lg"></i>
              Book Appointment
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              { icon: 'ri-shield-check-fill', label: 'Licensed NRS240A' },
              { icon: 'ri-timer-flash-line', label: 'Same-Day Available' },
              { icon: 'ri-map-pin-fill', label: '4525 S Sandhill Rd, LV' },
              { icon: 'ri-award-fill', label: '10+ Years Experience' },
              { icon: 'ri-star-fill', label: '4.9★ Rated Service' },
            ].map((b, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-white text-xs font-semibold"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <i className={`${b.icon} text-sm`} style={{ color: '#E87722' }}></i>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="w-full border-b" style={{ backgroundColor: '#fff', borderColor: '#e5e7eb' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4" style={{ borderColor: '#e5e7eb' }}>
            {[
              { num: '5,000+', label: 'Customers Served', icon: 'ri-group-fill' },
              { num: '30 Min', label: 'Avg. Transaction Time', icon: 'ri-timer-flash-fill' },
              { num: '4.9 ★', label: 'Customer Rating', icon: 'ri-star-fill' },
              { num: '10+ Yrs', label: 'Serving Las Vegas', icon: 'ri-award-fill' },
            ].map((stat, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-center py-5 px-4 text-center border-r last:border-r-0"
                style={{ borderColor: '#e5e7eb' }}
              >
                <i className={`${stat.icon} text-xl mb-1`} style={{ color: '#E87722' }}></i>
                <p className="text-2xl font-black" style={{ color: '#001f4d', fontFamily: "'Inter', sans-serif" }}>{stat.num}</p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DISCLAIMER BANNER ── */}
      <div className="w-full py-3 border-b" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-2 text-center text-sm">
          <i className="ri-information-fill flex-shrink-0" style={{ color: '#E87722' }}></i>
          <p className="text-gray-700">
            <strong style={{ color: '#001f4d' }}>DMV-Xpress Services is a licensed third-party document preparation service (NRS240A).</strong>
            {' '}We are NOT the Nevada Department of Motor Vehicles.{' '}
            <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold" style={{ color: '#001f4d' }}>Visit dmv.nv.gov for official DMV services →</a>
          </p>
        </div>
      </div>

      {/* ── QUICK LINKS STRIP ── */}
      <section className="py-8 border-b" style={{ backgroundColor: '#f8f9fa', borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {QUICK_LINKS.map((ql, i) => (
              <Link
                key={i}
                to={ql.to}
                className="flex flex-col items-center text-center p-3 rounded-lg border bg-white hover:border-orange-400 transition-all group cursor-pointer"
                style={{ borderColor: '#e5e7eb' }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-colors" style={{ backgroundColor: '#EEF4FF' }}>
                  <i className={`${ql.icon} text-xl`} style={{ color: '#003366' }}></i>
                </div>
                <span className="text-xs font-semibold text-gray-700 leading-tight group-hover:text-orange-600 transition-colors">{ql.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ACCORDION ── */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bordered title — dmv.nv.gov style */}
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
            Our Services — What We Do For You.
          </h2>
          <p className="text-gray-600 mt-4 mb-8 text-base max-w-3xl">
            As a licensed Nevada Document Preparation Service, we handle every step of your DMV transaction — from paperwork to submission. Select a service below to see what&apos;s included.
          </p>

          <div className="grid md:grid-cols-2 gap-0 border rounded-lg overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            {ACCORDION_ITEMS.map((item, i) => (
              <div key={i} className="border-b last:border-b-0 md:odd:border-r" style={{ borderColor: '#e5e7eb' }}>
                <button
                  onClick={() => setOpenAccordion(openAccordion === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-bold text-base" style={{ color: '#003366' }}>{item.title}</span>
                  {openAccordion === i
                    ? <i className="ri-arrow-up-s-line text-xl flex-shrink-0" style={{ color: '#E87722' }}></i>
                    : <i className="ri-arrow-down-s-line text-xl flex-shrink-0" style={{ color: '#E87722' }}></i>
                  }
                </button>
                {openAccordion === i && (
                  <div className="px-6 pb-5 border-t" style={{ borderColor: '#f3f4f6', backgroundColor: '#fafafa' }}>
                    <ul className="mt-3 space-y-2">
                      {item.links.map((link, j) => (
                        <li key={j}>
                          <Link
                            to={link.to}
                            className="flex items-center gap-2 text-sm hover:underline"
                            style={{ color: '#003366' }}
                          >
                            <i className="ri-arrow-right-s-line flex-shrink-0" style={{ color: '#E87722' }}></i>
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Service cards grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {SERVICES.map((svc, i) => (
              <div key={i} className="border rounded-lg overflow-hidden hover:border-orange-300 transition-all group" style={{ borderColor: '#e5e7eb' }}>
                <div className="px-5 py-4 flex items-center gap-3" style={{ backgroundColor: '#003366' }}>
                  <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E87722' }}>
                    <i className={`${svc.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-white font-bold text-base">{svc.title}</h3>
                </div>
                <div className="p-5">
                  <ul className="space-y-2 mb-4">
                    {svc.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-arrow-right-s-line flex-shrink-0 mt-0.5" style={{ color: '#E87722' }}></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={svc.slug}
                    className="inline-flex items-center gap-1 text-sm font-bold hover:underline"
                    style={{ color: '#003366' }}
                  >
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPOINTMENT SECTION ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
            Make An Appointment.
          </h2>
          <div className="mt-5 grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-gray-700 text-base mb-4 leading-relaxed">
                All services at DMV-Xpress are <strong>by appointment only</strong>. Call us at{' '}
                <a href="tel:7028028696" className="font-bold underline" style={{ color: '#003366' }}>702.802.8696</a>{' '}
                to schedule. Same-day appointments are available when slots are open and confirmed by phone.
              </p>
              <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                Not sure what documents to bring? We&apos;ll walk you through exactly what you need before your visit — no wasted trips, no rejected applications.
              </p>
              <div className="rounded-lg p-4 mb-5" style={{ backgroundColor: '#FFF8F0', border: '1px solid #fde8cc' }}>
                <p className="text-sm font-bold mb-1" style={{ color: '#E87722' }}>
                  <i className="ri-information-fill mr-1"></i>Appointments Required
                </p>
                <p className="text-gray-600 text-sm">
                  Please call before visiting. Walk-ins are not accepted. Same-day must be confirmed by phone.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="tel:7028028696"
                  className="flex items-center justify-center gap-2 text-white font-bold py-3 rounded text-sm transition-all whitespace-nowrap"
                  style={{ backgroundColor: '#E87722' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}
                >
                  <i className="ri-phone-fill"></i>Call to Schedule
                </a>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 font-bold py-3 rounded text-sm border-2 transition-all whitespace-nowrap"
                  style={{ color: '#003366', borderColor: '#003366' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#003366'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#003366'; }}
                >
                  <i className="ri-calendar-line"></i>Contact Us
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {STEPS.map((step, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border" style={{ borderColor: '#e5e7eb' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#003366' }}>
                      <i className={`${step.icon} text-white text-base`}></i>
                    </div>
                    <span className="text-2xl font-black" style={{ color: '#e5e7eb', fontFamily: "'Source Sans Pro', sans-serif" }}>{step.num}</span>
                  </div>
                  <h4 className="font-bold text-sm mb-1" style={{ color: '#003366' }}>{step.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HAVE YOUR DOCUMENTS READY ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
            Have Your Documents Ready.
          </h2>
          <p className="text-gray-600 mt-4 mb-8 text-base max-w-3xl">
            Want faster service when you arrive? Have your paperwork ready before your appointment. All Nevada DMV forms are available on our{' '}
            <Link to="/forms" className="font-bold underline" style={{ color: '#003366' }}>DMV Forms page</Link>.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { service: 'Vehicle Registration', docs: ['Nevada Certificate of Title or out-of-state title', 'Proof of Nevada liability insurance', 'Government-issued photo ID', 'Emissions test certificate (Clark County)', 'VIN inspection form (out-of-state only)'], to: '/services/vehicle-registration' },
              { service: 'Title Transfer', docs: ['Original Nevada Certificate of Title (signed by seller)', 'Odometer disclosure statement', 'Bill of Sale (VP-104)', 'Proof of Nevada liability insurance', 'Government-issued photo ID (buyer & seller)'], to: '/services/title-transfer' },
              { service: 'Duplicate Title', docs: ['Application for Duplicate Title (VP-012)', 'Government-issued photo ID', 'Current vehicle registration (if available)', 'Notarized affidavit (if lien present)', 'Police report (if title was stolen)'], to: '/services/duplicate-title' },
              { service: 'License Plates', docs: ['Current vehicle registration', 'Government-issued photo ID', 'Vehicle title (for new plates)', 'Proof of Nevada insurance', 'Police report (stolen plates only)'], to: '/services/license-plates' },
              { service: 'Temporary Permits', docs: ['Vehicle title or current registration', 'Proof of Nevada liability insurance', 'Government-issued photo ID', 'VIN verification (30-day, out-of-state only)'], to: '/services/temporary-permits' },
              { service: 'Notary Services', docs: ['Government-issued photo ID (all signers)', 'Original documents — do NOT sign before arriving', 'Vehicle title (for title notarization)', 'All parties present (if applicable)'], to: '/services/notary' },
            ].map((item, i) => (
              <div key={i} className="border rounded-lg p-5 hover:border-orange-300 transition-all" style={{ borderColor: '#e5e7eb' }}>
                <h3 className="font-bold text-base mb-3 flex items-center gap-2" style={{ color: '#003366' }}>
                  <i className="ri-file-list-3-line" style={{ color: '#E87722' }}></i>
                  {item.service}
                </h3>
                <ul className="space-y-1.5 mb-4">
                  {item.docs.map((doc, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <i className="ri-arrow-right-s-line flex-shrink-0 mt-0.5" style={{ color: '#E87722' }}></i>
                      {doc}
                    </li>
                  ))}
                </ul>
                <Link to={item.to} className="text-sm font-bold hover:underline flex items-center gap-1" style={{ color: '#003366' }}>
                  Full Requirements <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT CAN WE HELP YOU WITH — navy wave section ── */}
      <section className="py-16 relative overflow-hidden" style={{ backgroundColor: '#003366' }}>
        {/* Wave top */}
        <div className="absolute top-0 left-0 right-0 h-10 overflow-hidden">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-full" style={{ fill: '#fff' }}>
            <path d="M0,0 C360,40 1080,40 1440,0 L1440,0 L0,0 Z"></path>
          </svg>
        </div>
        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-10 overflow-hidden">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-full" style={{ fill: '#fff' }}>
            <path d="M0,40 C360,0 1080,0 1440,40 L1440,40 L0,40 Z"></path>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6">
          <h2 className="text-3xl font-black text-white mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
            What Can We Help You With?
          </h2>
          <p className="text-white/70 mt-4 mb-10 text-base max-w-2xl">
            Whether you&apos;re a new Nevada resident, buying a car, or need a replacement title — we have you covered.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HELP_CARDS.map((card, i) => (
              <div key={i} className="rounded-lg p-6 border border-white/10 hover:border-orange-400 transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded flex items-center justify-center mb-4" style={{ backgroundColor: '#E87722' }}>
                  <i className={`${card.icon} text-white text-lg`}></i>
                </div>
                <h4 className="text-white font-bold text-base mb-2">{card.title}</h4>
                <p className="text-white/65 text-sm leading-relaxed mb-4">{card.body}</p>
                <Link
                  to={card.link}
                  className="inline-flex items-center gap-1 text-sm font-bold hover:underline"
                  style={{ color: '#E87722' }}
                >
                  {card.linkLabel} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
                Why Las Vegas Drivers Trust Us.
              </h2>
              <p className="text-gray-600 mt-4 mb-7 text-base leading-relaxed">
                As a licensed <strong>Nevada Document Preparation Service (NRS240A)</strong>, we bridge the gap between Las Vegas drivers and the Nevada DMV — saving you hours, rejections, and frustration since 2014.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-time-fill', title: 'Avoid the 3-Hour Nevada DMV Wait', desc: 'Walk in, get served in under 1 hour on average. We process your DMV transaction without the government queue.' },
                  { icon: 'ri-shield-check-fill', title: 'Licensed Under NRS240A', desc: 'State-licensed and bonded document preparation service. Your data and documents handled professionally.' },
                  { icon: 'ri-file-check-fill', title: 'First-Time Right — No Rejections', desc: 'We know every Nevada DMV form, requirement, and fee. Documents prepared correctly the first time.' },
                  { icon: 'ri-customer-service-2-fill', title: 'Real People, Personal Service', desc: 'Call and speak directly with our team. Every client gets personalized guidance — not automated systems.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF4FF' }}>
                      <i className={`${item.icon} text-lg`} style={{ color: '#003366' }}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">{item.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Comparison table */}
            <div className="rounded-lg overflow-hidden border" style={{ borderColor: '#e5e7eb' }}>
              <div className="grid grid-cols-3 text-center" style={{ backgroundColor: '#003366' }}>
                <div className="p-3 border-r border-white/10"></div>
                <div className="p-3 border-r border-white/10">
                  <p className="text-white font-bold text-sm">DMV-Xpress</p>
                  <p className="text-white/50 text-xs">Third-Party Prep</p>
                </div>
                <div className="p-3">
                  <p className="text-white/70 font-bold text-sm">Nevada DMV</p>
                  <p className="text-white/40 text-xs">dmv.nv.gov</p>
                </div>
              </div>
              {[
                { label: 'Avg. Wait Time', us: '≤ 1 Hour', dmv: '2–4+ Hours' },
                { label: 'Document Guidance', us: '✓ Full checklist', dmv: 'General info' },
                { label: 'Forms Prepared For You', us: '✓ We prepare all', dmv: 'Self-service' },
                { label: 'Title Notarization', us: '✓ On-site', dmv: 'Not offered' },
                { label: 'Driver\'s License / ID', us: '✗ Not offered', dmv: '✓ Available' },
                { label: 'OHV Registration', us: '✗ Go to DMV', dmv: '✓ Available' },
                { label: 'Fleet / Multi-Vehicle', us: '✓ Same-day', dmv: 'Separate appts' },
              ].map((row, i) => (
                <div key={i} className="grid grid-cols-3 text-center border-t" style={{ borderColor: '#f3f4f6', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <div className="p-3 text-left border-r" style={{ borderColor: '#f3f4f6' }}>
                    <p className="text-gray-700 text-xs font-medium">{row.label}</p>
                  </div>
                  <div className="p-3 border-r" style={{ borderColor: '#f3f4f6', backgroundColor: '#FFF8F0' }}>
                    <p className="text-xs font-bold" style={{ color: row.us.startsWith('✗') ? '#9ca3af' : '#003366' }}>{row.us}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-gray-500 text-xs">{row.dmv}</p>
                  </div>
                </div>
              ))}
              <div className="p-3 text-center border-t" style={{ borderColor: '#f3f4f6', backgroundColor: '#f8f9fa' }}>
                <p className="text-gray-400 text-xs">DMV-Xpress is not affiliated with the Nevada DMV. <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline" style={{ color: '#003366' }}>dmv.nv.gov</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
            What Las Vegas Drivers Say.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {TESTIMONIALS.map((t, i) => (
              <article key={i} className="bg-white rounded-lg p-5 border hover:border-orange-300 transition-all" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, j) => <i key={j} className="ri-star-fill text-sm" style={{ color: '#E87722' }}></i>)}
                </div>
                <p className="text-gray-600 text-sm italic leading-relaxed mb-4">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3 pt-3 border" style={{ borderColor: '#f3f4f6' }}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm" style={{ backgroundColor: '#003366' }}>
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.role} · {t.date}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATION ── */}
      <section className="py-14 border-t" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-2 pb-3" style={{ borderBottom: '4px solid #E87722', display: 'inline-block', fontFamily: "'Source Sans Pro', sans-serif" }}>
            Visit Our Las Vegas Office.
          </h2>
          <p className="text-gray-600 mt-4 mb-8 text-base">4525 S Sandhill Rd — between Flamingo &amp; Tropicana, easy free parking. <strong>By appointment only.</strong></p>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-lg overflow-hidden border" style={{ borderColor: '#e5e7eb' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.8948108432234!2d-115.06516!3d36.08398!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c0b5c1234567%3A0x1234567890abcdef!2s4525%20S%20Sandhill%20Rd%2C%20Las%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1234567890123"
                width="100%" height="320"
                style={{ border: 0, display: 'block' }}
                allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DMV-Xpress Services — 4525 S Sandhill Rd Las Vegas NV"
              ></iframe>
            </div>
            <div className="space-y-4">
              {[
                { icon: 'ri-map-pin-fill', label: 'Address', val: '4525 S Sandhill Rd, Las Vegas, NV 89121', href: undefined },
                { icon: 'ri-phone-fill', label: 'Phone', val: '702.802.8696', href: 'tel:7028028696' },
                { icon: 'ri-mail-fill', label: 'Email', val: 'info@dmv-xpress.com', href: 'mailto:info@dmv-xpress.com' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
                  <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF4FF' }}>
                    <i className={`${item.icon} text-xl`} style={{ color: '#003366' }}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-0.5">{item.label}</p>
                    {item.href
                      ? <a href={item.href} className="font-semibold text-sm hover:underline" style={{ color: '#003366' }}>{item.val}</a>
                      : <p className="font-semibold text-sm text-gray-800">{item.val}</p>}
                  </div>
                </div>
              ))}
              <div className="p-4 rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-time-fill" style={{ color: '#003366' }}></i>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Business Hours</p>
                </div>
                <div className="space-y-1 text-sm">
                  {[['Mon – Fri', '9:00 AM – 5:00 PM'], ['Saturday', '10:00 AM – 3:00 PM'], ['Sunday', 'Closed']].map(([d, h], i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-gray-500">{d}:</span>
                      <span className="font-semibold" style={{ color: h === 'Closed' ? '#E87722' : '#003366' }}>{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <Footer />
    </div>
  );
}
